import { AsyncPaginatedRequest } from "./";
import { LazyAsyncIter } from "@/iterators";
import MonthPeriod from "@/entities/month-period";
import { CamelCasedProperties } from "type-fest";
import { pageMapperBuilder, Paginated } from "@/pagination";
import HttpServiceBase from "./http-service";

export default class EntityComplianceService extends HttpServiceBase {
  public getEntitiesComplianceAsync(
    period: MonthPeriod,
    types: SearchEntitiesParams = {},
    percentange: number
  ): AsyncIterator<Paginated<EntityIdWithCompliance>> {
    const params = {
      period_month: period.monthIndex,
      period_year: period.year,
      type_ids: types?.type_ids?.join(","),
      percentage_compliance: percentange,
    };

    const iter = new AsyncPaginatedRequest<EntityIdWithComplianceDTO>(
      "get",
      "entities-compliance",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();
    return new LazyAsyncIter(iter).map(
      pageMapperBuilder(entityIdWithComplianceFromDTO)
    );
  }

  public getComplianceSummaryExportLink(
    period: MonthPeriod,
    entityTypeIds: number[],
    percentageFilterId: number
  ): string {
    const base = this.httpClient.defaults.baseURL ?? "";
    const params = {
      period_month: period.monthIndex,
      period_year: period.year,
      type_ids: entityTypeIds.join(","),
      percentage_compliance: percentageFilterId,
    };

    const query = Object.entries(params)
      .map(([key, val]) => `${key}=${val}`)
      .join("&");
    return `${base}/entities-compliance/export?${query}`;
  }

  public async getEntityComplianceSummary(
    entityId: number,
    period: MonthPeriod
  ): Promise<ComplianceSummary[]> {
    const result = await this.httpClient.get<ComplianceSummaryDTO[]>(
      `entities-compliance/${entityId}`,
      { params: { period: period.toYMStr(), with_submitter_names: "1" } }
    );
    return result.data.map(complianceSummaryFromDTO);
  }
}

export interface EntityIdWithComplianceDTO {
  entity_id: number;
  compliance_score: number;
  percentage: number;
}

export type EntityIdWithCompliance =
  CamelCasedProperties<EntityIdWithComplianceDTO>;

export function entityIdWithComplianceFromDTO(
  dto: EntityIdWithComplianceDTO
): EntityIdWithCompliance {
  return {
    entityId: dto.entity_id,
    complianceScore: dto.compliance_score,
    percentage: dto.percentage,
  };
}

interface ComplianceSummaryDTO {
  documentation_id: number;
  has_comply: boolean;
  submitted_by_id: null | number;
  submitter_first_name: null | string;
  submitter_last_name: null | string;
  last_update_date: string | null;
  submitted: boolean
}

export type ComplianceSummary = CamelCasedProperties<
  Omit<ComplianceSummaryDTO, "last_update_date">
> & { lastUpdateDate: Date | null };

export function complianceSummaryFromDTO(
  dto: ComplianceSummaryDTO
): ComplianceSummary {
  return {
    documentationId: dto.documentation_id,
    hasComply: dto.has_comply,
    submittedById: dto.submitted_by_id,
    submitterFirstName: dto.submitter_first_name,
    submitterLastName: dto.submitter_last_name,
    lastUpdateDate:
      dto.last_update_date == null
        ? null
        : new Date(Date.parse(dto.last_update_date)),
    submitted: dto.submitted
  };
}

export interface SearchEntitiesParams {
  ids?: number[];
  name?: string;
  acronym?: string;
  type_ids?: number[];
  name_or_acronym?: string;
}
